import TagManager from "react-gtm-module";

/**
 * 
 
 *
 * @param {string} action - Snake_case event_action identifier
 * @param {string} [label=""] - event_label value; omitted from the payload if falsy
 * @param {Object} [extraLabels={}] - Additional dataLayer fields, e.g.
 *   { event_label1: email, event_label2: phone }
 */
const GTM_ID = "GTM-TH4RWGG";

let isGtmInitialized = false;

// Sets up the GTM container/dataLayer without pushing a trackable event —
// use on page mount so the container exists before the first real event
// (e.g. edit_demo, start) fires, without recording an extra "page load" event.
export const initGtm = () => {
  try {
    if (isGtmInitialized) return;
    
    const alreadyOnPage =
      (typeof window !== "undefined" && window.google_tag_manager?.[GTM_ID]) ||
      (typeof document !== "undefined" &&
        document.querySelector(`script[src*="gtm.js?id=${GTM_ID}"]`));
    if (alreadyOnPage) {
      isGtmInitialized = true;
      return;
    }
    TagManager.initialize({ gtmId: GTM_ID });
    isGtmInitialized = true;
  } catch (_) {
    // GTM should never break the calling flow
  }
};

/**
 * The GTM container name for the surface the event actually happened on:
 * "codeyoung_portal" when Sandbox runs embedded inside StudentPortal
 * (StudentPortal's SandboxRenderer sets `window.__SANDBOX_IS_PLUGIN__` before
 * loading this bundle), otherwise "codeyoung_sandbox" for standalone Sandbox.
 *
 * Mirrors getAuthSource() in Auth/AuthFlow/constants.js, which makes the same
 * embedded-vs-standalone call for the cy-sso request origin.
 *
 * @returns {"codeyoung_portal"|"codeyoung_sandbox"}
 */
export const getGtmSource = () =>
  typeof window !== "undefined" && window.__SANDBOX_IS_PLUGIN__ === true
    ? "codeyoung_portal"
    : "codeyoung_sandbox";

/**
 * Pushes one fully-formed dataLayer payload, exactly once.
 *
 * saveGTMEvents calls both TagManager.dataLayer() AND TagManager.initialize()
 * with the same args. Passing a dataLayer to initialize() makes it inject a
 * script that pushes the payload a second time, so every event through that
 * helper lands in window.dataLayer twice (same gtm.uniqueEventId on both).
 * Here the container is initialized once, without a dataLayer, and the event is
 * pushed on its own.
 *
 * @param {Object} dataLayer - the complete payload, event/event_category included
 */
export const pushGtmEvent = (dataLayer) => {
  try {
    initGtm();
    TagManager.dataLayer({ dataLayer });
  } catch (_) {
    // GTM should never break the calling flow
  }
};

export const fireSourceAwareGtm = (action, label = "", extraLabels = {}) => {
  try {
    const source = getGtmSource();
    const payload = {
      event: source,
      event_category: source,
      event_action: action,
      ...(label ? { event_label: label } : {}),
      ...extraLabels,
    };
    // Through pushGtmEvent so the container load goes via the guarded initGtm.
    // This used to call TagManager.initialize(args) on the first event, which
    // both pushed the payload a second time and loaded a second copy of the
    // container when StudentPortal's index.html snippet had already loaded one.
    pushGtmEvent(payload);
  } catch (_) {
    // GTM should never break the calling flow
  }
};
